/**
 * L'archivio documenti del cliente: le piccole cose che servono a più componenti.
 *
 * Formati ammessi, peso massimo e categorie NON stanno qui: arrivano dal
 * database (`documenti_regole()`) dentro la risposta dell'archivio. Qui si
 * leggono e basta, così un formato nuovo si aggiunge in un posto solo.
 */
import { api } from "@/lib/api";

/**
 * L'errore del server in una frase. Un 422 di validazione di FastAPI ha come
 * `detail` un ELENCO di oggetti: passato così a un toast fa cadere la pagina.
 */
export function erroreDocumenti(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (Array.isArray(d)) return "Manca qualcosa: controlla il file e la categoria.";
  if (e?.response?.status === 413) return "Il file è troppo grande.";
  if (e?.message === "Network Error") return "Il server non risponde: controlla la connessione e riprova.";
  return "Qualcosa non ha funzionato. Riprova.";
}

const fmtPeso = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 1 });

/** «840 KB», «3,2 MB». */
export function pesoFile(byte) {
  const n = Number(byte);
  if (byte === null || byte === undefined || !Number.isFinite(n)) return "";
  if (n < 1024 * 1024) return `${fmtPeso.format(Math.max(1, Math.round(n / 1024)))} KB`;
  return `${fmtPeso.format(n / (1024 * 1024))} MB`;
}

export const eImmagine = (mime) => (mime || "").startsWith("image/");

/** L'attributo `accept` del campo file, dalle regole del database. */
export function accettati(regole) {
  if (!regole) return undefined;
  return regole.formati.flatMap((f) => [f.mime, ...f.estensioni.map((x) => `.${x}`)]).join(",");
}

/** Cosa non va in un file prima ancora di mandarlo (l'ultima parola resta al server). */
export function problemaFile(file, regole) {
  if (!file || !regole) return null;
  if (file.size === 0) return "Il file è vuoto.";
  if (file.size > regole.max_byte) return `Il file pesa ${pesoFile(file.size)}: il massimo è ${regole.max_testo}.`;
  const estensione = (file.name.split(".").pop() || "").toLowerCase();
  const ok = regole.formati.some((f) => f.mime === file.type || f.estensioni.includes(estensione));
  return ok ? null : `Questo formato non si carica. Vanno bene ${regole.formati_testo}.`;
}

/** Il nome senza l'estensione: «rilievo-cucina.pdf» → «rilievo-cucina». */
export function senzaEstensione(nome) {
  const i = (nome || "").lastIndexOf(".");
  return i > 0 ? nome.slice(0, i) : nome || "";
}

/** «IMG_0412.jpg», «image.jpg», «scansione.pdf»: nomi che non dicono niente. */
export function nomeAnonimo(nome) {
  return /^(img|image|photo|foto|pxl|dsc|scan|scansione|documento|file)[-_ ]?[\d_-]*$/i.test(senzaEstensione(nome).trim());
}

export function etichettaCategoria(regole, codice) {
  return regole?.categorie.find((c) => c.codice === codice)?.etichetta || "Documento";
}

/**
 * Apre una versione in una scheda nuova. La finestra si apre SUBITO, dentro il
 * clic: aperta dopo l'attesa del link, Safari sul telefono la blocca come popup.
 * Il link scade in un minuto: si chiede adesso, non prima.
 */
export async function apriVersione(id) {
  const finestra = window.open("", "_blank");
  try {
    const r = await api.get(`/documenti/versioni/${id}/link`);
    if (finestra) {
      finestra.opener = null;
      finestra.location.replace(r.data.url);
    } else {
      window.location.assign(r.data.url);
    }
  } catch (e) {
    finestra?.close();
    throw e;
  }
}

/** Scarica una versione col suo nome. Il server risponde «da salvare»: la pagina resta dov'è. */
export async function scaricaVersione(id) {
  const r = await api.get(`/documenti/versioni/${id}/link`, { params: { scarica: true } });
  window.location.assign(r.data.url);
}

/** Una riga dello storico, detta come la direbbe una persona. */
export function fraseEvento(e) {
  const chi = e.autore_nome || "Il sistema";
  switch (e.evento) {
    case "caricato":
      return `${chi} ha caricato il documento`;
    case "nuova_versione":
      return `${chi} ha caricato la versione ${e.versione}`;
    case "tolto":
      return `${chi} l'ha tolto dall'archivio`;
    case "rimesso":
      return `${chi} l'ha rimesso nell'archivio`;
    case "eliminato":
      return `${chi} l'ha cancellato`;
    default:
      return chi;
  }
}
